import { EditOutlined, LinkOutlined, AuditOutlined } from "@ant-design/icons";
import {
  Table,
  Tabs,
  Select,
  Tag,
  Space,
  Button,
  Input,
  DatePicker,
  Tooltip,
  Badge,
  Empty,
  Grid
} from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";

import ReviewModal from "./ReviewModal";
import { showErrorNotification, showSuccessNotification } from "../../../utils/notification";
import { clamp } from "../../../utils/responsiveHelpers";
import ProblemFormModal from "../Catalogue/ProblemFormModal";
import {
  abortLatestRequest,
  ALL_PLATFORM_FILTER_OPTIONS,
  beginLatestRequest,
  DATE_DISPLAY_FORMAT,
  EXTERNAL_PROBLEMS_API_BASE,
  EXTERNAL_PROBLEMS_PAGINATION_CONFIG,
  MIN_DISTINCT_REQUESTER_OPTIONS,
  QUEUE_SORT_BY_ENUM,
  QUEUE_TABS,
  REQUEST_STATUS_DISPLAY_CONFIG,
  REQUEST_STATUS_ENUM,
  reportUnlessCancelled,
  SHARED_INFO_TEXT,
  sumColumnWidths,
  COLUMN_WIDTH_ENUM
} from "../helper";
import InfoHint, { LabelWithHint } from "../InfoHint";

const { Search } = Input;
const { RangePicker } = DatePicker;

/** Column widths of the queue table; `scroll.x` is their sum. */
const QUEUE_TABLE_COLUMN_WIDTHS = Object.freeze({
  link: COLUMN_WIDTH_ENUM.LINK,
  platform: COLUMN_WIDTH_ENUM.SHORT,
  status: COLUMN_WIDTH_ENUM.DATE,
  externalId: COLUMN_WIDTH_ENUM.ID,
  distinctRequesterCount: COLUMN_WIDTH_ENUM.WIDE,
  firstRequestedAt: COLUMN_WIDTH_ENUM.SHORT,
  lastRequestedAt: COLUMN_WIDTH_ENUM.SHORT,
  actions: COLUMN_WIDTH_ENUM.COUNT
});

const STATUS_COUNTS_INITIAL_STATE = Object.fromEntries(
  Object.values(REQUEST_STATUS_ENUM).map((status) => [status, 0])
);

const QUEUE_FILTER_INITIAL_STATE = Object.freeze({
  platform: undefined,
  minDistinctRequesterCount: undefined,
  requestedAfter: undefined,
  requestedBefore: undefined,
  searchTerm: ""
});

/**
 * Sums a tab's covered statuses out of the full `statusCounts` payload, so the
 * "Unresolved" and "Closed without fix" tabs show one combined badge instead
 * of one per status.
 *
 * @param {string[]} statuses
 * @param {Record<string, number>} statusCounts
 * @returns {number}
 */
const sumTabCount = (statuses, statusCounts) =>
  statuses.length === 0
    ? Object.values(statusCounts).reduce((total, count) => total + count, 0)
    : statuses.reduce((total, status) => total + (statusCounts[status] ?? 0), 0);

/**
 * The ops reconciliation queue: every requested-but-missing problem link,
 * grouped into a tab strip by status rather than a dropdown, so a growing
 * queue is visible without clicking in.
 *
 * Each tab's status set is joined into the comma-separated form
 * `GET /requests` accepts, so every tab, single- or multi-status alike, is
 * one server-paginated request.
 *
 * @param {object} props
 * @param {string} props.ROOT_URL
 * @param {boolean} props.hasManagementAccess - False leaves the screen read-only, matching the
 *   admin's `externalProblems.hasManagementAccess` permission.
 */
const ReconciliationQueue = ({ hasManagementAccess, ROOT_URL }) => {
  const [activeTabKey, setActiveTabKey] = useState(QUEUE_TABS[0].key);
  const [filters, setFilters] = useState(QUEUE_FILTER_INITIAL_STATE);
  const [sortBy, setSortBy] = useState(QUEUE_SORT_BY_ENUM.RECENTLY_REQUESTED);
  const [currentPage, setCurrentPage] = useState(EXTERNAL_PROBLEMS_PAGINATION_CONFIG.DEFAULT_PAGE);
  const [pageSize, setPageSize] = useState(EXTERNAL_PROBLEMS_PAGINATION_CONFIG.DEFAULT_PAGE_SIZE);

  const [queueRequests, setQueueRequests] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [statusCounts, setStatusCounts] = useState(STATUS_COUNTS_INITIAL_STATE);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedList, setHasLoadedList] = useState(false);

  const [reviewingRequest, setReviewingRequest] = useState(null);
  const [isReviewSubmitting, setIsReviewSubmitting] = useState(false);

  const [formModalUniquePublicId, setFormModalUniquePublicId] = useState(null);
  const [formModalPrefill, setFormModalPrefill] = useState(null);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);

  const abortControllerRef = useRef(null);

  const screens = Grid.useBreakpoint();
  const isMobileView = screens.md === false;

  const columns = [
    {
      title: "Link",
      dataIndex: "normalizedUrl",
      key: "normalizedUrl",
      width: QUEUE_TABLE_COLUMN_WIDTHS.link,
      render: (normalizedUrl) => (
        <a
          href={normalizedUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ wordBreak: "break-all" }}
        >
          <LinkOutlined /> {normalizedUrl}
        </a>
      )
    },
    {
      title: <LabelWithHint label="Platform" infoText={SHARED_INFO_TEXT.PLATFORM} />,
      dataIndex: "platformDisplayName",
      key: "platformDisplayName",
      width: QUEUE_TABLE_COLUMN_WIDTHS.platform,
      responsive: ["sm"],
      render: (platformDisplayName) => <Tag color="blue">{platformDisplayName}</Tag>
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: QUEUE_TABLE_COLUMN_WIDTHS.status,
      responsive: ["sm"],
      render: (status) => {
        const { helpText, color, label } = REQUEST_STATUS_DISPLAY_CONFIG[status] ?? {};

        return (
          <Tooltip title={helpText}>
            <Tag color={color}>{label}</Tag>
          </Tooltip>
        );
      }
    },
    {
      title: "External ID",
      dataIndex: "externalId",
      key: "externalId",
      width: QUEUE_TABLE_COLUMN_WIDTHS.externalId,
      responsive: ["md"]
    },
    {
      title: (
        <LabelWithHint
          label="Distinct Students"
          infoText={SHARED_INFO_TEXT.DISTINCT_REQUESTER_COUNT}
        />
      ),
      dataIndex: "distinctRequesterCount",
      key: "distinctRequesterCount",
      width: QUEUE_TABLE_COLUMN_WIDTHS.distinctRequesterCount,
      align: "center",
      responsive: ["md"],
      sorter: true,
      sortDirections: ["descend"]
    },
    {
      title: "First Requested",
      dataIndex: "firstRequestedAt",
      key: "firstRequestedAt",
      width: QUEUE_TABLE_COLUMN_WIDTHS.firstRequestedAt,
      responsive: ["lg"],
      render: (firstRequestedAt) => dayjs(firstRequestedAt).format(DATE_DISPLAY_FORMAT)
    },
    {
      title: "Last Requested",
      dataIndex: "lastRequestedAt",
      key: "lastRequestedAt",
      width: QUEUE_TABLE_COLUMN_WIDTHS.lastRequestedAt,
      responsive: ["lg"],
      render: (lastRequestedAt) => dayjs(lastRequestedAt).format(DATE_DISPLAY_FORMAT)
    },
    {
      title: "Actions",
      key: "actions",
      width: QUEUE_TABLE_COLUMN_WIDTHS.actions,
      fixed: "right",
      render: (_, queueRequest) => (
        <Space>
          <Tooltip title="Open problem editor">
            <Button
              type="text"
              shape="circle"
              icon={<EditOutlined />}
              disabled={!hasManagementAccess}
              onClick={() => handleOpenEditor(queueRequest)}
            />
          </Tooltip>
          <Tooltip title="Review">
            <Button
              type="text"
              shape="circle"
              icon={<AuditOutlined />}
              disabled={!hasManagementAccess}
              onClick={() => setReviewingRequest(queueRequest)}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  const tabItems = QUEUE_TABS.map(({ key, label, description, statuses }) => ({
    key,
    label: (
      <Space size={6}>
        {label}
        <InfoHint title={description} />
        {hasLoadedList && <Badge count={sumTabCount(statuses, statusCounts)} overflowCount={999} showZero />}
      </Space>
    )
  }));

  /**
   * Fetches one page of the queue for the active tab. The tab's status set
   * is joined into the comma-separated form the endpoint accepts; an empty
   * set (the "All" tab) omits the param entirely rather than sending it
   * empty. Only the latest call writes state, and a failure leaves the rows
   * on screen.
   */
  const fetchQueueRows = useCallback(() => {
    const { statuses } = QUEUE_TABS.find((tab) => tab.key === activeTabKey);
    const {
      platform,
      minDistinctRequesterCount,
      requestedAfter,
      requestedBefore,
      searchTerm
    } = filters;

    const { signal } = beginLatestRequest(abortControllerRef);

    setIsLoading(true);
    axios
      .get(`${ROOT_URL}/${EXTERNAL_PROBLEMS_API_BASE}/requests`, {
        signal,
        params: {
          status: statuses.length > 0 ? statuses.join(",") : undefined,
          platform,
          minDistinctRequesterCount,
          requestedAfter,
          requestedBefore,
          searchTerm: searchTerm || undefined,
          sortBy,
          page: currentPage,
          limit: pageSize
        }
      })
      .then((response) => {
        const { requests, statusCounts: fetchedStatusCounts, pagination } = response.data;
        setQueueRequests(requests);
        setStatusCounts(fetchedStatusCounts);
        setTotalItems(pagination.totalItems);
        setHasLoadedList(true);
      })
      .catch(reportUnlessCancelled)
      .finally(() => {
        if (!signal.aborted) {
          setIsLoading(false);
        }
      });
  }, [ROOT_URL, activeTabKey, filters, sortBy, currentPage, pageSize]);

  /**
   * Switches the active status tab and returns the pager to its first page.
   *
   * @param {string} tabKey - A key of `QUEUE_TABS`.
   */
  const handleTabChange = (tabKey) => {
    setActiveTabKey(tabKey);
    setCurrentPage(EXTERNAL_PROBLEMS_PAGINATION_CONFIG.DEFAULT_PAGE);
  };

  /**
   * Merges a partial filter change into the current filters and returns the
   * pager to its first page.
   *
   * @param {Partial<typeof QUEUE_FILTER_INITIAL_STATE>} filterPatch
   */
  const updateFilter = (filterPatch) => {
    setFilters((previousFilters) => ({ ...previousFilters, ...filterPatch }));
    setCurrentPage(EXTERNAL_PROBLEMS_PAGINATION_CONFIG.DEFAULT_PAGE);
  };

  /**
   * Applies the requested-date range as whole-day bounds, or clears both bounds.
   *
   * @param {Array<import("dayjs").Dayjs>|null} dates - Start and end of the range, null when cleared.
   */
  const handleRequestedRangeChange = (dates) => {
    if (!dates) {
      updateFilter({ requestedAfter: undefined, requestedBefore: undefined });
      return;
    }

    const [rangeStart, rangeEnd] = dates;
    updateFilter({
      requestedAfter: rangeStart.startOf("day").toISOString(),
      requestedBefore: rangeEnd.endOf("day").toISOString()
    });
  };

  /**
   * Applies the table's page, page size and sort. A sorted column orders by
   * distinct requesters; unsorted orders by most recent request.
   *
   * @param {object} pagination - antd's pager state.
   * @param {number} pagination.current - Page the table moved to.
   * @param {number} pagination.pageSize - Rows per page after the change.
   * @param {object} _tableFilters - Unused; filters are held in state.
   * @param {{order?: string}} sorter
   */
  const handleTableChange = (
    { current: nextPage, pageSize: nextPageSize },
    _tableFilters,
    { order }
  ) => {
    const nextSortBy = order
      ? QUEUE_SORT_BY_ENUM.DISTINCT_REQUESTERS
      : QUEUE_SORT_BY_ENUM.RECENTLY_REQUESTED;

    setSortBy(nextSortBy);
    setCurrentPage(nextSortBy === sortBy ? nextPage : EXTERNAL_PROBLEMS_PAGINATION_CONFIG.DEFAULT_PAGE);
    setPageSize(nextPageSize);
  };

  /**
   * Applies a review decision to the row under review, then reloads the queue.
   *
   * @param {string} action - The status the admin sets on the row.
   */
  const handleReviewSubmit = (action) => {
    const { uniquePublicId } = reviewingRequest;

    setIsReviewSubmitting(true);
    axios
      .patch(`${ROOT_URL}/${EXTERNAL_PROBLEMS_API_BASE}/requests/${uniquePublicId}`, {
        status: action
      })
      .then(() => {
        showSuccessNotification("Review decision applied successfully!");
        setReviewingRequest(null);
        fetchQueueRows();
      })
      .catch(showErrorNotification)
      .finally(() => setIsReviewSubmitting(false));
  };

  /**
   * Opens the problem editor for a queue row. A row that already points at a
   * document (`resolvedExternalProblemUniquePublicId` set, always true for
   * `INCOMPLETE_DATA`) opens that document for editing; creating a new one
   * instead would fail on a duplicate key and leave the row stuck open
   * forever. Every other row has nothing to edit yet, so it opens a blank
   * create form prefilled with what the queue already parsed.
   *
   * @param {object} queueRequest - One reconciliation-queue request.
   */
  const handleOpenEditor = ({ resolvedExternalProblemUniquePublicId, platform, externalId, normalizedUrl }) => {
    if (resolvedExternalProblemUniquePublicId) {
      setFormModalPrefill(null);
      setFormModalUniquePublicId(resolvedExternalProblemUniquePublicId);
      setIsFormModalOpen(true);
      return;
    }

    setFormModalPrefill({
      platform,
      externalId,
      externalUrl: normalizedUrl
    });
    setFormModalUniquePublicId(null);
    setIsFormModalOpen(true);
  };

  /** Closes the review modal without applying a decision. */
  const closeReviewModal = () => setReviewingRequest(null);

  /** Closes the problem editor without saving. */
  const closeFormModal = () => setIsFormModalOpen(false);

  /** Closes the problem editor and reloads the queue after a save. */
  const handleSaved = () => {
    setIsFormModalOpen(false);
    fetchQueueRows();
  };

  useEffect(() => {
    fetchQueueRows();
  }, [fetchQueueRows]);

  useEffect(() => () => abortLatestRequest(abortControllerRef), []);

  return (
    <div style={{ padding: clamp(12, 24) }}>
      <Tabs activeKey={activeTabKey} items={tabItems} onChange={handleTabChange} />

      <div style={{ marginBottom: 16, display: "flex", flexWrap: "wrap", gap: 12 }}>
        <Search
          placeholder="Search by link"
          allowClear
          style={{ minWidth: 200, flex: 1, maxWidth: 260 }}
          onSearch={(searchTerm) => updateFilter({ searchTerm })}
        />
        <Select
          placeholder="Platform"
          allowClear
          style={{ minWidth: 160, flex: 1, maxWidth: 260 }}
          options={ALL_PLATFORM_FILTER_OPTIONS}
          onChange={(platform) => updateFilter({ platform })}
        />
        <Select
          placeholder="Min. distinct students"
          allowClear
          style={{ minWidth: 180, flex: 1, maxWidth: 260 }}
          options={MIN_DISTINCT_REQUESTER_OPTIONS}
          onChange={(minDistinctRequesterCount) => updateFilter({ minDistinctRequesterCount })}
        />
        <RangePicker
          placeholder={["Requested after", "Requested before"]}
          style={{ minWidth: 220, flex: 1 }}
          onChange={handleRequestedRangeChange}
        />
      </div>

      <Table
        rowKey="uniquePublicId"
        columns={columns}
        dataSource={queueRequests}
        loading={isLoading}
        locale={{
          emptyText:
            hasLoadedList && !isLoading ? (
              <Empty description="No requests match this tab and these filters." />
            ) : (
              <div style={{ minHeight: 120 }} />
            )
        }}
        bordered
        size={isMobileView ? "small" : "middle"}
        onChange={handleTableChange}
        pagination={{
          current: currentPage,
          pageSize,
          total: totalItems,
          showSizeChanger: !isMobileView,
          simple: isMobileView,
          pageSizeOptions: EXTERNAL_PROBLEMS_PAGINATION_CONFIG.PAGE_SIZE_OPTIONS
        }}
        scroll={{ x: isMobileView ? "max-content" : sumColumnWidths(QUEUE_TABLE_COLUMN_WIDTHS) }}
      />

      {reviewingRequest && (
        <ReviewModal
          open={Boolean(reviewingRequest)}
          onClose={closeReviewModal}
          onSubmit={handleReviewSubmit}
          isSubmitting={isReviewSubmitting}
        />
      )}

      {isFormModalOpen && (
        <ProblemFormModal
          open={isFormModalOpen}
          uniquePublicId={formModalUniquePublicId}
          prefill={formModalPrefill}
          onClose={closeFormModal}
          onSaved={handleSaved}
          ROOT_URL={ROOT_URL}
        />
      )}
    </div>
  );
};

export default ReconciliationQueue;
